import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleHelpIcon, CircleInfoIcon } from '@persona-id/design-tokens/icons/16px';
import { compact, difference, intersection, partition, sortBy, uniq } from 'lodash/fp';
import { ReactNode, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import {
  PermissionAction,
  PermissionObjectType,
  PermissionRowDataType,
  PermissionScope,
  PermissionSectionDataType,
  PermissionTableDataType,
  ScopedPermission,
} from 'dashboard/components/PermissionsTable/constants';
import { CASE_DETAILS_ROW_NAME } from 'dashboard/components/PermissionsTable/ProductsSectionData';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import MultiSelect from 'dashboard/components-lib/MultiSelect';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { RolePermission } from 'dashboard/models/role';
import { StyledCheckboxCell, StyledTableRow } from 'dashboard/views/DashboardRolesShowView/styles';
import Checkbox from 'design-system/components/Checkbox';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
} from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import useVerificationTemplateReferenceMap from 'lib/hooks/useVerificationTemplateReferenceMap';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const ALL_CURRENT_TEMPLATES = 'ALL_CURRENT_TEMPLATES';
const ALL_CURRENT_AND_FUTURE_TEMPLATES = 'ALL_CURRENT_AND_FUTURE_TEMPLATES';

enum CaseAssignment {
  Assigned = 'Assigned',
  AllCases = 'All',
}

const getScopeObjects = (scopes: PermissionScope[], objects: PermissionObjectType[]) => {
  return compact(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    scopes.flatMap<PermissionObjectType>((scope) => {
      switch (scope) {
        case PermissionScope.AccountType:
          return objects.filter((object) => object.type === ApiModel.AccountType);
        case PermissionScope.CaseAssignmentPolicy:
          return objects.filter((object) => object.type === ApiModel.CaseAssignmentPolicy);
        case PermissionScope.CaseTemplate:
          return objects.filter((object) => object.type === ApiModel.CaseTemplate);
        case PermissionScope.CaseQueue:
          return objects.filter((object) => object.type === ApiModel.CaseQueue);
        case PermissionScope.LegacyExternalIntegration:
          return objects.filter((object) => object.type.startsWith('external-integration/'));
        case PermissionScope.InquiryTemplate:
          return objects.filter(
            (object) =>
              object.type === ApiModel.InquiryTemplate || object.type === ApiModel.Template,
          );
        case PermissionScope.List:
          return objects.filter((object) => object.type.startsWith('list/'));
        case PermissionScope.ReportTemplate:
          return objects.filter((object) => object.type.startsWith('report-template/'));
        case PermissionScope.TableView:
          return objects.filter((object) => object.type === ApiModel.TableView);
        case PermissionScope.Team:
          return objects.filter((object) => object.type === ApiModel.Team);
        case PermissionScope.TransactionType:
          return objects.filter((object) => object.type === ApiModel.TransactionType);
        case PermissionScope.VerificationTemplate:
          return objects.filter((object) => object.type.startsWith('verification-template/'));
        case PermissionScope.Workflow:
          return objects.filter((object) => object.type === ApiModel.Workflow);
      }
    }),
  );
};

export interface PermissionsTableProps {
  disabled: boolean;
  isAdmin: boolean;
  objects?: PermissionObjectType[];
  rolePermissions: RolePermission[];
  schema: PermissionTableDataType;
  sectionTitle: string;
  selectedEnvironmentIds: string[];
  setRolePermissions: (permissions: RolePermission[]) => void;
}

const PermissionsTable = ({
  disabled,
  isAdmin,
  objects = [],
  rolePermissions = [],
  schema,
  sectionTitle,
  selectedEnvironmentIds,
  setRolePermissions,
}: PermissionsTableProps) => {
  const actions: PermissionAction[] = useMemo(() => {
    const actionsByColumn = Object.values(PermissionAction);

    // Sorting actions here to make sure View is left of Edit.
    return uniq(
      schema.sections.flatMap((section) =>
        section.subSections.flatMap((subSection) =>
          subSection.rows.flatMap((r) => r.actions.map((a) => a.label)),
        ),
      ),
    ).sort((a, b) => actionsByColumn.indexOf(a) - actionsByColumn.indexOf(b));
  }, [schema]);

  const vtmplRefMap = useVerificationTemplateReferenceMap();
  const allScopes = uniq(
    schema.sections
      .flatMap((section) => section.subSections)
      .flatMap((subSection) => subSection.rows)
      .flatMap((row) => row.actions)
      .flatMap((action) => action.permissions)
      .flatMap((scopedPermission) => scopedPermission.scopes),
  );

  return (
    <Table>
      <TableHead>
        <StyledTableRow>
          {actions.map((action) => (
            <TableHeaderCell key={`permissions-table__header--${action}`}>
              <Paragraph color='primary'>{action}</Paragraph>
              <Spacer width={0.5} height={0} />
            </TableHeaderCell>
          ))}
          <StyledPermissionNameHeaderCell>
            <Spacer width={5} height={0} />
            <Paragraph color='primary'>{sectionTitle}</Paragraph>
            {sectionTitle === 'Product' ? (
              <>
                <Spacer width={0.5} height={0} />
                <Tooltip side='bottom' iconTrigger={<CircleInfoIcon />}>
                  Create: Create a new record or template <br /> List: View lists of records or
                  templates <br /> View: View details of a specific record or template <br /> Edit:
                  Configure templates or update records
                </Tooltip>
              </>
            ) : null}
          </StyledPermissionNameHeaderCell>
          <TableHeaderCell>
            {allScopes.length > 1 && <Paragraph color='primary'>Configuration</Paragraph>}
          </TableHeaderCell>
        </StyledTableRow>
      </TableHead>
      <TableBody>
        {schema.sections.map((section) => (
          <Section
            key={`permissions-table__section--${section.label}`}
            actions={actions}
            disabled={disabled}
            isAdmin={isAdmin}
            objects={objects}
            rolePermissions={rolePermissions}
            section={section}
            searchTerm=''
            selectedEnvironmentIds={selectedEnvironmentIds}
            setRolePermissions={setRolePermissions}
            title={section.label}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            tooltip={section.tooltip}
            vtmplRefMap={vtmplRefMap}
            relatedPermissionsWarning={
              section.subSections[0].scopes.includes(PermissionScope.InquiryTemplate) ? (
                <RelatedVTPermissionsWarning objects={objects} rolePermissions={rolePermissions} />
              ) : null
            }
          />
        ))}
      </TableBody>
    </Table>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const vtmplLabelStyles = css`
  display: -webkit-box;
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`;

const Title = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  ${vtmplLabelStyles};
`;

const UsageReference = styled(Paragraph).attrs(() => ({ size: 'sm' }))`
  ${vtmplLabelStyles};
`;

const Token = styled(Paragraph).attrs(() => ({ size: 'sm' }))`
  color: ${Color.CoreSlate500};
`;

interface SectionProps {
  actions: PermissionAction[];
  isAdmin: boolean;
  disabled: boolean;
  objects: PermissionObjectType[];
  section: PermissionSectionDataType;
  searchTerm: string;
  selectedEnvironmentIds?: string[];
  rolePermissions: RolePermission[];
  title: string;
  tooltip: string;
  vtmplRefMap: Record<string, string>;
  relatedPermissionsWarning?: ReactNode;
  setRolePermissions: (rolePermissions: RolePermission[]) => void;
}

const Section = ({
  actions,
  disabled,
  isAdmin,
  objects = [],
  rolePermissions,
  section,
  selectedEnvironmentIds,
  title,
  tooltip,
  vtmplRefMap,
  relatedPermissionsWarning,
  setRolePermissions,
}: SectionProps) => {
  const allSectionRows = section.subSections.flatMap((subsection) => subsection.rows);

  // Section logic is only concerned with first subsection's objects as it contains the scope
  // to affect the selected templates dropdown
  const primaryScopeObjects = getScopeObjects(section.subSections[0].scopes, objects);
  const primaryScope = section.subSections[0].scopes.find((s) => s !== PermissionScope.Environment);

  const [accordionExpanded, setAccordionExpanded] = useState<boolean>(false);

  const currentCaseAssignment = useMemo(() => {
    if (
      rolePermissions.some((rp) =>
        [Permission.CaseAllList, Permission.CaseAllView].includes(rp.name),
      )
    ) {
      return CaseAssignment.AllCases;
    }

    return CaseAssignment.Assigned;
  }, [rolePermissions]);

  const possibleScopedPermissionsFor = ({
    action,
    rows,
  }: {
    action: PermissionAction;
    rows: PermissionRowDataType[];
  }): ScopedPermission[] => {
    let scopedPermissions = rows
      .flatMap((row) => row.actions)
      .filter((a) => a.label === action)
      .flatMap((a) => a.permissions);

    /*
     * James: One-off to handle two interdependent cases permissions in the
     *        backend. case.all.view handles viewing all cases for a template,
     *        and we only add it if the dropdown indicates all cases instead of
     *        cases assigned to user. case.view is checked in either case since
     *        it's already inside of the ProductSectionData schema.
     */
    if (currentCaseAssignment === CaseAssignment.AllCases) {
      if (action === PermissionAction.View && rows.some((r) => r.label === CASE_DETAILS_ROW_NAME)) {
        scopedPermissions = [
          ...scopedPermissions,
          {
            permission: Permission.CaseAllView,
            scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
          },
        ];
      } else if (
        action === PermissionAction.List &&
        rows.some((r) => r.label === CASE_DETAILS_ROW_NAME)
      ) {
        scopedPermissions = [
          ...scopedPermissions,
          {
            permission: Permission.CaseAllList,
            scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
          },
        ];
      }
    }

    return scopedPermissions;
  };

  const possiblePermissionNamesFor = ({
    action,
    rows,
  }: {
    action: PermissionAction;
    rows: PermissionRowDataType[];
  }): Permission[] => {
    return possibleScopedPermissionsFor({ action, rows }).flatMap((sp) => sp.permission);
  };

  const permissionsByScope = ({
    permission,
    scopes,
    objectIds,
    addAllCurrentAndFutureTemplates,
  }: {
    permission: Permission;
    scopes: PermissionScope[];
    objectIds?: string[];
    addAllCurrentAndFutureTemplates?: boolean;
  }): RolePermission[] => {
    if (scopes.length === 0) {
      return [
        {
          name: permission,
          'environment-id': null,
          'object-id': null,
        },
      ];
    } else if (scopes.includes(PermissionScope.Environment)) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const environmentIds = selectedEnvironmentIds.length > 0 ? selectedEnvironmentIds : [null];

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return environmentIds.flatMap((environmentId) => {
        if (
          addAllCurrentAndFutureTemplates ||
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          objectIds.length === 0 ||
          scopes.filter((s) => s !== PermissionScope.Environment).length === 0
        ) {
          return {
            name: permission,
            'environment-id': environmentId,
            'object-id': null,
          };
        } else {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          return objectIds.flatMap((objectId) => {
            return {
              name: permission,
              'environment-id': environmentId,
              'object-id': objectId,
            };
          });
        }
      });
    } else {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (addAllCurrentAndFutureTemplates || objectIds.length === 0) {
        return [
          {
            name: permission,
            'environment-id': null,
            'object-id': null,
          },
        ];
      } else {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return objectIds.flatMap((objectId) => {
          return {
            name: permission,
            'environment-id': null,
            'object-id': objectId,
          };
        });
      }
    }
  };

  const possibleRolePermissionsFor = ({
    action,
    rows,
    objectIds = [],
    addAllCurrentAndFutureTemplates = false,
  }: {
    action: PermissionAction;
    rows: PermissionRowDataType[];
    objectIds?: string[];
    addAllCurrentAndFutureTemplates?: boolean;
  }): RolePermission[] => {
    return possibleScopedPermissionsFor({ action, rows }).flatMap((scopedPermission) => {
      const { permission, scopes } = scopedPermission;

      return permissionsByScope({
        permission,
        scopes,
        objectIds,
        addAllCurrentAndFutureTemplates,
      });
    });
  };

  const selectedObjectIdsByScope = useMemo(() => {
    const subsectionScopes = compact(
      section.subSections.map((subsection) =>
        subsection.scopes.find((scope) => scope !== PermissionScope.Environment),
      ),
    );

    const result: Record<PermissionScope | string, string[]> = {};

    if (subsectionScopes.length === 0) {
      return result;
    }

    const allSelectedObjectIds = uniq(
      rolePermissions.filter((rp) => rp['object-id'] != null).map((rp) => rp['object-id']),
    );

    subsectionScopes.forEach((scope) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      result[scope] = intersection(
        getScopeObjects([scope], objects).map((object) => object.id),
        allSelectedObjectIds,
      );
    });

    return result;
  }, [rolePermissions, objects, section.subSections]);

  const addAllCurrentAndFutureTemplatesChecked = useMemo(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => (selectedObjectIdsByScope[primaryScope] || []).length === 0,
    [selectedObjectIdsByScope, primaryScope],
  );

  const addAllCurrentTemplatesChecked = useMemo(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => (selectedObjectIdsByScope[primaryScope] || []).length === primaryScopeObjects.length,
    [selectedObjectIdsByScope, primaryScope, primaryScopeObjects],
  );

  const allSelected = ({
    action,
    rows,
  }: {
    action: PermissionAction;
    rows: PermissionRowDataType[];
  }) => {
    const possiblePermissionNames = possiblePermissionNamesFor({
      action,
      rows,
    });

    if (possiblePermissionNames.length === 0) {
      return false;
    }

    return (
      possiblePermissionNames.length ===
      intersection(
        possiblePermissionNames,
        rolePermissions.map((rp) => rp.name),
      ).length
    );
  };

  const someSelected = ({
    action,
    rows,
  }: {
    action: PermissionAction;
    rows: PermissionRowDataType[];
  }) => {
    return (
      intersection(
        possiblePermissionNamesFor({ action, rows: rows }),
        rolePermissions.map((rp) => rp.name),
      ).length > 0
    );
  };

  const noneSelected = ({
    action,
    rows,
  }: {
    action: PermissionAction;
    rows: PermissionRowDataType[];
  }) => {
    return (
      intersection(
        possiblePermissionNamesFor({ action, rows }),
        rolePermissions.map((rp) => rp.name),
      ).length === 0
    );
  };

  const handleCheckboxSelect = ({
    action,
    rows,
  }: {
    action: PermissionAction;
    rows: PermissionRowDataType[];
  }) => {
    let possiblePermissionNames = possiblePermissionNamesFor({
      action,
      rows,
    });

    const hasCaseDetailsRow = rows.some((r) => r.label === CASE_DETAILS_ROW_NAME);

    if (hasCaseDetailsRow && action === PermissionAction.View) {
      possiblePermissionNames = [...possiblePermissionNames, Permission.CaseAllView];
    } else if (hasCaseDetailsRow && action === PermissionAction.List) {
      possiblePermissionNames = [...possiblePermissionNames, Permission.CaseAllList];
    }

    /*
     * James: Remove all role permissions without name matching to clean up
     * permissions that may be hidden from the frontend. For example, we might
     * not be passing in inactive templates to this component, or a customer may
     * be in some weird migration state that we can now correct by ticking
     * checkboxes in the UI.
     *
     * Note: If the view box is unchecked, we'll also remove edit permissions.
     */
    const remainingRolePermissions = rolePermissions
      .filter((rp: RolePermission) => !possiblePermissionNames.includes(rp.name))
      .filter((rp: RolePermission) => {
        return (
          action !== PermissionAction.View ||
          !possiblePermissionNamesFor({
            action: PermissionAction.Edit,
            rows,
          }).includes(rp.name)
        );
      });

    if (someSelected({ action, rows })) {
      setRolePermissions(remainingRolePermissions);
    } else {
      setRolePermissions([
        ...remainingRolePermissions,
        ...section.subSections.flatMap((subsection) => {
          const scope = subsection.scopes.find((scope) => scope !== PermissionScope.Environment);
          const rowLabels = rows.map((row) => row.label);

          return possibleRolePermissionsFor({
            action,
            rows: subsection.rows.filter((row) => rowLabels.includes(row.label)),
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            objectIds: selectedObjectIdsByScope[scope],
          });
        }),
      ]);
    }
  };

  const handleObjectMultiSelect = ({
    formValues,
    rows,
    objectTypes,
    addAllCurrentAndFutureTemplatesChecked,
    addAllCurrentTemplatesChecked,
  }: {
    formValues: string[];
    rows: PermissionRowDataType[];
    objectTypes: PermissionObjectType[];
    addAllCurrentAndFutureTemplatesChecked: boolean;
    addAllCurrentTemplatesChecked: boolean;
  }) => {
    const filteredObjectIds = formValues.filter(
      (value) => ![ALL_CURRENT_TEMPLATES, ALL_CURRENT_AND_FUTURE_TEMPLATES].includes(value),
    );

    const possiblePermissionNames = actions.flatMap((action) =>
      possiblePermissionNamesFor({ action, rows }),
    );

    const [existingRolePermissions, remainingRolePermissions] = partition(
      (rp) => possiblePermissionNames.includes(rp.name),
      rolePermissions,
    );

    const existingRolePermissionNames = uniq(existingRolePermissions.map((rp) => rp.name));

    if (
      (formValues.includes(ALL_CURRENT_AND_FUTURE_TEMPLATES) &&
        !addAllCurrentAndFutureTemplatesChecked) ||
      (!formValues.includes(ALL_CURRENT_TEMPLATES) && addAllCurrentTemplatesChecked) ||
      (filteredObjectIds.length === 0 && !addAllCurrentAndFutureTemplatesChecked)
    ) {
      const possibleRolePermissions = actions.flatMap((action) =>
        possibleRolePermissionsFor({
          action,
          addAllCurrentAndFutureTemplates: true,
          rows,
        }),
      );
      setRolePermissions([
        ...remainingRolePermissions,
        ...possibleRolePermissions.filter((rp) => existingRolePermissionNames.includes(rp.name)),
      ]);
    } else if (
      (formValues.includes(ALL_CURRENT_TEMPLATES) && !addAllCurrentTemplatesChecked) ||
      (!formValues.includes(ALL_CURRENT_AND_FUTURE_TEMPLATES) &&
        addAllCurrentAndFutureTemplatesChecked)
    ) {
      const objectIds = objectTypes.map((object) => object.id);

      const newRolePermissions = actions
        .flatMap((action) => possibleRolePermissionsFor({ action, objectIds, rows }))
        .filter((rp) => existingRolePermissionNames.includes(rp.name));

      const remainingRolePermissions = rolePermissions.filter(
        (rp) => !possiblePermissionNames.includes(rp.name),
      );

      setRolePermissions([...remainingRolePermissions, ...newRolePermissions]);
    } else {
      const newRolePermissions = actions
        .flatMap((action) =>
          possibleRolePermissionsFor({
            action,
            objectIds: filteredObjectIds,
            rows,
          }),
        )
        .filter((rp) => existingRolePermissionNames.includes(rp.name));

      const remainingRolePermissions = rolePermissions.filter(
        (rp) => !possiblePermissionNames.includes(rp.name),
      );

      setRolePermissions([...remainingRolePermissions, ...newRolePermissions]);
    }
  };

  const multiSelectOnChange = (formValues: string[]) => {
    handleObjectMultiSelect({
      formValues,
      objectTypes: primaryScopeObjects,
      rows: allSectionRows,
      addAllCurrentAndFutureTemplatesChecked,
      addAllCurrentTemplatesChecked,
    });
  };

  const multiSelectLabelTextMapper = (formValues: string[]) => {
    const filteredObjectIds = formValues.filter(
      (value) => ![ALL_CURRENT_TEMPLATES, ALL_CURRENT_AND_FUTURE_TEMPLATES].includes(value),
    );

    switch (primaryScope) {
      case PermissionScope.CaseTemplate:
      case PermissionScope.InquiryTemplate:
      case PermissionScope.ReportTemplate:
      case PermissionScope.VerificationTemplate:
        if (isAdmin || addAllCurrentAndFutureTemplatesChecked)
          return 'All current and future templates';
        // Non-admin users have no idea what the total number of templates is.
        if (isAdmin && addAllCurrentTemplatesChecked) return 'All current templates';
        if (filteredObjectIds.length === 0) return 'Restrict to templates';
        if (filteredObjectIds.length === 1) return '1 template';
        return `${filteredObjectIds.length} templates`;
      case PermissionScope.LegacyExternalIntegration:
        if (isAdmin || addAllCurrentAndFutureTemplatesChecked)
          return 'All current and future connections';
        // Non-admin users have no idea what the total number of external integrations is.
        if (isAdmin && addAllCurrentTemplatesChecked) return 'All current connections';
        if (filteredObjectIds.length === 0) return 'Restrict to connections';
        if (filteredObjectIds.length === 1) return '1 connection';
        return `${filteredObjectIds.length} connections`;
      case PermissionScope.List:
        if (isAdmin || addAllCurrentAndFutureTemplatesChecked)
          return 'All current and future lists';
        // Non-admin users have no idea what the total number of lists is.
        if (isAdmin && addAllCurrentTemplatesChecked) return 'All current lists';
        if (filteredObjectIds.length === 0) return 'Restrict to lists';
        if (filteredObjectIds.length === 1) return '1 list';
        return `${filteredObjectIds.length} lists`;
      case PermissionScope.Team:
        if (isAdmin || addAllCurrentAndFutureTemplatesChecked)
          return 'All current and future teams';
        // Non-admin users have no idea what the total number of teams is.
        if (isAdmin && addAllCurrentTemplatesChecked) return 'All current teams';
        if (filteredObjectIds.length === 0) return 'Restrict to teams';
        if (filteredObjectIds.length === 1) return '1 team';
        return `${filteredObjectIds.length} teams`;
      case PermissionScope.AccountType:
      case PermissionScope.TransactionType:
        if (isAdmin || addAllCurrentAndFutureTemplatesChecked)
          return 'All current and future types';
        // Non-admin users have no idea what the total number of types is.
        if (isAdmin && addAllCurrentTemplatesChecked) return 'All current types';
        if (filteredObjectIds.length === 0) return 'Restrict to types';
        if (filteredObjectIds.length === 1) return '1 type';
        return `${filteredObjectIds.length} types`;
      case PermissionScope.Workflow:
        if (isAdmin || addAllCurrentAndFutureTemplatesChecked)
          return 'All current and future workflows';
        // Non-admin users have no idea what the total number of workflows is.
        if (isAdmin && addAllCurrentTemplatesChecked) return 'All current workflows';
        if (filteredObjectIds.length === 0) return 'Restrict to workflows';
        if (filteredObjectIds.length === 1) return '1 workflow';
        return `${filteredObjectIds.length} workflows`;
      case PermissionScope.TableView:
        if (isAdmin || addAllCurrentAndFutureTemplatesChecked)
          return 'All current and future table views';
        // Non-admin users have no idea what the total number of table views is.
        if (isAdmin && addAllCurrentTemplatesChecked) return 'All current table views';
        if (filteredObjectIds.length === 0) return 'Restrict to table views';
        if (filteredObjectIds.length === 1) return '1 table view';
        return `${filteredObjectIds.length} table views`;
      case PermissionScope.CaseAssignmentPolicy:
        if (isAdmin || addAllCurrentAndFutureTemplatesChecked)
          return 'All current and future policies';
        // Non-admin users have no idea what the total number of policies is.
        if (isAdmin && addAllCurrentTemplatesChecked) return 'All current policies';
        if (filteredObjectIds.length === 0) return 'Restrict to policies';
        if (filteredObjectIds.length === 1) return '1 policy';
        return `${filteredObjectIds.length} policies`;
      case PermissionScope.CaseQueue:
        if (isAdmin || addAllCurrentAndFutureTemplatesChecked)
          return 'All current and future queues';
        // Non-admin users have no idea what the total number of queues is.
        if (isAdmin && addAllCurrentTemplatesChecked) return 'All current queues';
        if (filteredObjectIds.length === 0) return 'Restrict to queues';
        if (filteredObjectIds.length === 1) return '1 queue';
        return `${filteredObjectIds.length} queues`;
    }
  };

  const selectAllLabelTextMapper = () => {
    switch (primaryScope) {
      case PermissionScope.CaseTemplate:
      case PermissionScope.InquiryTemplate:
      case PermissionScope.ReportTemplate:
      case PermissionScope.VerificationTemplate:
        return 'All current templates';
      case PermissionScope.LegacyExternalIntegration:
        return 'All current connections';
      case PermissionScope.List:
        return 'All current lists';
      case PermissionScope.Team:
        return 'All current teams';
      case PermissionScope.AccountType:
      case PermissionScope.TransactionType:
        return 'All current types';
      case PermissionScope.Workflow:
        return 'All current workflows';
      case PermissionScope.TableView:
        return 'All current table views';
      case PermissionScope.CaseAssignmentPolicy:
        return 'All current policies';
      case PermissionScope.CaseQueue:
        return 'All current queues';
    }
  };

  const addAllCurrentAndFutureTemplatesCheckboxLabelTextMapper = () => {
    switch (primaryScope) {
      case PermissionScope.CaseTemplate:
      case PermissionScope.InquiryTemplate:
      case PermissionScope.ReportTemplate:
      case PermissionScope.VerificationTemplate:
        return 'All current and future templates';
      case PermissionScope.LegacyExternalIntegration:
        return 'All current and future connections';
      case PermissionScope.List:
        return 'All current and future lists';
      case PermissionScope.Team:
        return 'All current and future teams';
      case PermissionScope.AccountType:
      case PermissionScope.TransactionType:
        return 'All current and future types';
      case PermissionScope.Workflow:
        return 'All current and future workflows';
      case PermissionScope.TableView:
        return 'All current and future table views';
      case PermissionScope.CaseAssignmentPolicy:
        return 'All current and future policies';
      case PermissionScope.CaseQueue:
        return 'All current and future queues';
    }
  };

  const objectCustomLabelTextMapper = (object: PermissionObjectType, withSourceName: boolean) => {
    const sourceInquiryTemplateName = withSourceName ? vtmplRefMap[object.id] : null;
    return (
      <Wrapper>
        <Title>{object.attributes.name}</Title>
        {sourceInquiryTemplateName && <UsageReference>{sourceInquiryTemplateName}</UsageReference>}
        <Token>{object.id}</Token>
      </Wrapper>
    );
  };

  const noneSelectedInSection = actions.every((action) =>
    noneSelected({ action, rows: allSectionRows }),
  );
  const hasAccordion = allSectionRows.length > 1;

  // If view is not checked, edit should not be checked.
  const actionDisabled = ({
    action,
    rows,
  }: {
    action: PermissionAction;
    rows: PermissionRowDataType[];
  }) => {
    const rowActionLabels = rows.flatMap((r) => r.actions).map((a) => a.label);

    if (disabled || rowActionLabels.indexOf(action) === -1) {
      return true;
    }

    if (action === PermissionAction.View) {
      return false;
    }

    if (action === PermissionAction.Edit && rowActionLabels.includes(PermissionAction.View)) {
      return noneSelected({ action: PermissionAction.View, rows });
    }

    return false;
  };

  return (
    <>
      <StyledTableRow hoverable={hasAccordion}>
        {actions.map((action) => {
          return (
            <StyledCheckboxCell
              key={`permissions-table__section-checkbox-cell--${title}-${action}`}
            >
              {possiblePermissionNamesFor({ action, rows: allSectionRows }).length > 0 && (
                <StyledCheckbox
                  id={`permissions-table__section-checkbox--${title}-${action}`}
                  checked={isAdmin || allSelected({ action, rows: allSectionRows })}
                  indeterminate={
                    !isAdmin &&
                    !allSelected({ action, rows: allSectionRows }) &&
                    someSelected({ action, rows: allSectionRows })
                  }
                  disabled={actionDisabled({ action, rows: allSectionRows })}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCheckboxSelect({ action, rows: allSectionRows });
                  }}
                />
              )}
            </StyledCheckboxCell>
          );
        })}
        <StyledPermissionNameCell
          id={`permissions-table__section-table-accordion-cell--${title}`}
          onClick={() => setAccordionExpanded(!accordionExpanded)}
        >
          {hasAccordion ? (
            <Accordion
              padded={false}
              caretSize='md'
              overrideExpanded={accordionExpanded}
              titleContainerHeight={relatedPermissionsWarning != null ? '40px' : 'auto'}
              title={
                <StyledAccordionTitle>
                  <Paragraph color='primary'>{title}</Paragraph>
                  {tooltip != null && (
                    <>
                      <Spacer width={0.5} height={0} />
                      <StyledTooltip side='right' iconTrigger={<CircleHelpIcon />}>
                        <StyledTooltipInner>{tooltip}</StyledTooltipInner>
                      </StyledTooltip>
                    </>
                  )}
                </StyledAccordionTitle>
              }
            />
          ) : (
            <>
              <Spacer width={5} height={0} />
              <Paragraph color='primary'>{title}</Paragraph>
              {tooltip != null && (
                <>
                  <Spacer width={0.5} height={0} />
                  <StyledTooltip side='right' iconTrigger={<CircleHelpIcon />}>
                    <StyledTooltipInner>{tooltip}</StyledTooltipInner>
                  </StyledTooltip>
                </>
              )}
            </>
          )}
        </StyledPermissionNameCell>
        <StyledPermissionConfigCell>
          {primaryScopeObjects.length > 0 && (
            <>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              <StyledMultiSelect
                searchEnabled={true}
                optionSearchKey='label'
                disabled={disabled || noneSelectedInSection}
                labelTextMapper={multiSelectLabelTextMapper}
                onChange={multiSelectOnChange}
                size='sm'
                value={compact([
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  ...selectedObjectIdsByScope[primaryScope],
                  ...(addAllCurrentTemplatesChecked ? [ALL_CURRENT_TEMPLATES] : []),
                  ...(addAllCurrentAndFutureTemplatesChecked
                    ? [ALL_CURRENT_AND_FUTURE_TEMPLATES]
                    : []),
                ])}
                selectOptionValues={[
                  {
                    label: addAllCurrentAndFutureTemplatesCheckboxLabelTextMapper(),
                    value: ALL_CURRENT_AND_FUTURE_TEMPLATES,
                  },
                  {
                    label: selectAllLabelTextMapper(),
                    value: ALL_CURRENT_TEMPLATES,
                  },
                  ...sortBy(
                    (obj) => obj['label'],
                    primaryScopeObjects.map((object) => {
                      return {
                        label: `${object.attributes.name} (${object.id})`,
                        customLabel: objectCustomLabelTextMapper(
                          object,
                          primaryScopeObjects[0].type.startsWith('verification-template/'),
                        ),
                        value: object.id,
                      };
                    }),
                  ),
                ]}
              />
              {relatedPermissionsWarning}
            </>
          )}
        </StyledPermissionConfigCell>
      </StyledTableRow>
      {accordionExpanded &&
        hasAccordion &&
        section.subSections.map((subSection, i) =>
          subSection.rows.map((row) => {
            return (
              <ExpandedAccordionRow
                id={`permission-table__row--${row.label}`}
                key={`permission-table__row--${row.label}`}
                actions={actions}
                objects={objects}
                row={row}
                rolePermissions={rolePermissions}
                title={title}
                vtmplRefMap={vtmplRefMap}
                hasAdditionalScope={i !== 0}
                disabled={disabled}
                isAdmin={isAdmin}
                actionDisabled={actionDisabled}
                permissionsByScope={permissionsByScope}
                possiblePermissionNamesFor={possiblePermissionNamesFor}
                setRolePermissions={setRolePermissions}
                someSelected={someSelected}
                handleObjectMultiSelect={handleObjectMultiSelect}
                handleCheckboxSelect={handleCheckboxSelect}
              />
            );
          }),
        )}
    </>
  );
};

interface ExpandedAccordionRowProps {
  id: string;
  actions: PermissionAction[];
  objects: PermissionObjectType[];
  rolePermissions: RolePermission[];
  row: PermissionRowDataType;
  title: string;
  vtmplRefMap: Record<string, string>;
  disabled: boolean;
  hasAdditionalScope: boolean;
  isAdmin: boolean;
  actionDisabled: ({
    action,
    rows,
  }: {
    action: PermissionAction;
    rows: PermissionRowDataType[];
  }) => boolean;
  permissionsByScope: ({
    permission,
    scopes,
    objectIds,
    addAllCurrentAndFutureTemplates,
  }: {
    permission: Permission;
    scopes: PermissionScope[];
    objectIds?: string[];
    addAllCurrentAndFutureTemplates?: boolean;
  }) => RolePermission[];
  possiblePermissionNamesFor: ({
    action,
    rows,
  }: {
    action: PermissionAction;
    rows: PermissionRowDataType[];
  }) => Permission[];
  setRolePermissions: (rolePermissions: RolePermission[]) => void;
  someSelected: ({
    action,
    rows,
  }: {
    action: PermissionAction;
    rows: PermissionRowDataType[];
  }) => boolean;
  handleObjectMultiSelect: ({
    formValues,
    rows,
    objectTypes,
    addAllCurrentAndFutureTemplatesChecked,
    addAllCurrentTemplatesChecked,
  }: {
    formValues: string[];
    rows: PermissionRowDataType[];
    objectTypes: PermissionObjectType[];
    addAllCurrentAndFutureTemplatesChecked: boolean;
    addAllCurrentTemplatesChecked: boolean;
  }) => void;
  handleCheckboxSelect: ({
    action,
    rows,
  }: {
    action: PermissionAction;
    rows: PermissionRowDataType[];
  }) => void;
}

const ExpandedAccordionRow = ({
  id,
  actions,
  row,
  objects = [],
  rolePermissions,
  title,
  vtmplRefMap,
  disabled,
  hasAdditionalScope,
  isAdmin,
  actionDisabled,
  permissionsByScope,
  possiblePermissionNamesFor,
  setRolePermissions,
  someSelected,
  handleObjectMultiSelect,
  handleCheckboxSelect,
}: ExpandedAccordionRowProps) => {
  const scopeObjects = getScopeObjects(
    uniq(
      row.actions
        .flatMap((action) => action.permissions)
        .flatMap((scopedPermission) => scopedPermission.scopes),
    ),
    objects,
  );

  const primaryScope = row.actions
    .flatMap((action) => action.permissions)
    .flatMap((scopedPermission) => scopedPermission.scopes)
    .find((s) => s !== PermissionScope.Environment);

  const selectedObjectIds = useMemo(() => {
    const possibleObjectIds = scopeObjects.map((object) => object.id);

    return uniq(
      rolePermissions
        .map((rp) => rp['object-id'])
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        .filter((objectId) => possibleObjectIds.includes(objectId)),
    );
  }, [scopeObjects, rolePermissions]);

  const currentCaseAssignment = useMemo(() => {
    if (
      rolePermissions.some((rp) =>
        [Permission.CaseAllList, Permission.CaseAllView].includes(rp.name),
      )
    ) {
      return CaseAssignment.AllCases;
    }

    return CaseAssignment.Assigned;
  }, [rolePermissions]);

  const hasAnyCaseAssigneeScopedPermission = useMemo(() => {
    return rolePermissions.some((rp) =>
      [
        Permission.CaseList,
        Permission.CaseAllList,
        Permission.CaseView,
        Permission.CaseAllView,
      ].includes(rp.name as Permission),
    );
  }, [rolePermissions]);

  const handleCasesDropdownChange = (value: string) => {
    const remainingRolePermissions = rolePermissions.filter(
      (rp) =>
        ![
          Permission.CaseList,
          Permission.CaseAllList,
          Permission.CaseView,
          Permission.CaseAllView,
        ].includes(rp.name as Permission),
    );

    let newRolePermissions: RolePermission[] = [];

    if (rolePermissions.some((rp) => rp.name === Permission.CaseList)) {
      newRolePermissions = [
        ...newRolePermissions,
        ...permissionsByScope({
          permission: Permission.CaseList,
          scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          objectIds: selectedObjectIds,
          addAllCurrentAndFutureTemplates: addAllCurrentAndFutureTemplatesChecked,
        }),
        ...(value === CaseAssignment.AllCases
          ? permissionsByScope({
              permission: Permission.CaseAllList,
              scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              objectIds: selectedObjectIds,
              addAllCurrentAndFutureTemplates: addAllCurrentAndFutureTemplatesChecked,
            })
          : []),
      ];
    }

    if (rolePermissions.some((rp) => rp.name === Permission.CaseView)) {
      newRolePermissions = [
        ...newRolePermissions,
        ...permissionsByScope({
          permission: Permission.CaseView,
          scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          objectIds: selectedObjectIds,
          addAllCurrentAndFutureTemplates: addAllCurrentAndFutureTemplatesChecked,
        }),
        ...(value === CaseAssignment.AllCases
          ? permissionsByScope({
              permission: Permission.CaseAllView,
              scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              objectIds: selectedObjectIds,
              addAllCurrentAndFutureTemplates: addAllCurrentAndFutureTemplatesChecked,
            })
          : []),
      ];
    }

    setRolePermissions([...remainingRolePermissions, ...newRolePermissions]);
  };

  const addAllCurrentAndFutureTemplatesChecked = useMemo(
    () => selectedObjectIds.length === 0,
    [selectedObjectIds],
  );

  const addAllCurrentTemplatesChecked = useMemo(
    () => selectedObjectIds.length === scopeObjects.length,
    [selectedObjectIds, scopeObjects],
  );

  const multiSelectLabelTextMapper = (formValues: string[]) => {
    const filteredObjectIds = formValues.filter(
      (value) => ![ALL_CURRENT_TEMPLATES, ALL_CURRENT_AND_FUTURE_TEMPLATES].includes(value),
    );

    switch (primaryScope) {
      case PermissionScope.CaseAssignmentPolicy:
        if (isAdmin || addAllCurrentAndFutureTemplatesChecked)
          return 'All current and future case assignment policies';
        // on-admin users have no idea what the total number of case assignment policies is.
        if (isAdmin && addAllCurrentTemplatesChecked) return 'All current case assignment policies';
        if (filteredObjectIds.length === 0) return 'Restrict to case assignment policies';
        if (filteredObjectIds.length === 1) return '1 case assignment policy';
        return `${filteredObjectIds.length} case assignment policies`;
      case PermissionScope.CaseQueue:
        if (isAdmin || addAllCurrentAndFutureTemplatesChecked)
          return 'All current and future case queues';
        // on-admin users have no idea what the total number of case queues is.
        if (isAdmin && addAllCurrentTemplatesChecked) return 'All current case queues';
        if (filteredObjectIds.length === 0) return 'Restrict to case queues';
        if (filteredObjectIds.length === 1) return '1 case queue';
        return `${filteredObjectIds.length} case queues`;
      case PermissionScope.TableView:
        if (isAdmin || addAllCurrentAndFutureTemplatesChecked)
          return 'All current and future table views';
        // Non-admin users have no idea what the total number of table views is.
        if (isAdmin && addAllCurrentTemplatesChecked) return 'All current table views';
        if (filteredObjectIds.length === 0) return 'Restrict to table views';
        if (filteredObjectIds.length === 1) return '1 table view';
        return `${filteredObjectIds.length} table views`;
    }
  };

  const objectCustomLabelTextMapper = (object: PermissionObjectType, withSourceName: boolean) => {
    const sourceInquiryTemplateName = withSourceName ? vtmplRefMap[object.id] : null;
    return (
      <Wrapper>
        <Title>{object.attributes.name}</Title>
        {sourceInquiryTemplateName && <UsageReference>{sourceInquiryTemplateName}</UsageReference>}
        <Token>{object.id}</Token>
      </Wrapper>
    );
  };

  const selectAllLabelTextMapper = () => {
    switch (primaryScope) {
      case PermissionScope.CaseAssignmentPolicy:
        return 'All current case assignment policies';
      case PermissionScope.CaseQueue:
        return 'All current case queues';
      case PermissionScope.TableView:
        return 'All current table views';
    }
  };

  const addAllCurrentAndFutureTemplatesCheckboxLabelTextMapper = () => {
    switch (primaryScope) {
      case PermissionScope.CaseAssignmentPolicy:
        return 'All current case assignment policies';
      case PermissionScope.CaseQueue:
        return 'All current and future case queues';
      case PermissionScope.TableView:
        return 'All current and future table views';
    }
  };

  const noneSelectedInRow = !actions.some((action) => someSelected({ action, rows: [row] }));

  const multiSelectOnChange = (formValues: string[]) => {
    handleObjectMultiSelect({
      formValues,
      objectTypes: scopeObjects,
      rows: [row],
      addAllCurrentAndFutureTemplatesChecked,
      addAllCurrentTemplatesChecked,
    });
  };

  return (
    <StyledTableRow id={id} hoverable={false}>
      {actions.map((action) => {
        return (
          <StyledCheckboxCell key={`permission-row__${row.label}-${action}`}>
            {possiblePermissionNamesFor({ action, rows: [row] }).length > 0 && (
              <StyledCheckbox
                id={`permissions-table__checkbox-${title}-${action}-${row.label}`}
                checked={isAdmin || someSelected({ action, rows: [row] })}
                disabled={actionDisabled({ action, rows: [row] })}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCheckboxSelect({ action, rows: [row] });
                }}
              />
            )}
          </StyledCheckboxCell>
        );
      })}
      <StyledPermissionNameCell>
        <Spacer width={5} height={0} />
        <Paragraph color='primary'>{row.label}</Paragraph>
        {row.tooltip != null && (
          <>
            <Spacer width={0.5} height={0} />
            <StyledTooltip side='right' iconTrigger={<CircleHelpIcon />}>
              <StyledTooltipInner>{row.tooltip}</StyledTooltipInner>
            </StyledTooltip>
          </>
        )}
      </StyledPermissionNameCell>
      <StyledPermissionConfigCell>
        {row.label === CASE_DETAILS_ROW_NAME && (
          <StyledSelect
            disabled={disabled || !hasAnyCaseAssigneeScopedPermission}
            onValueChange={handleCasesDropdownChange}
            size='sm'
            placeholder='Cases assigned to user'
            value={currentCaseAssignment}
          >
            <Option value={CaseAssignment.Assigned}>Cases assigned to user</Option>
            <Option value={CaseAssignment.AllCases}>All cases</Option>
          </StyledSelect>
        )}
        {hasAdditionalScope && scopeObjects.length > 0 && (
          <>
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            <StyledMultiSelect
              searchEnabled={true}
              optionSearchKey='label'
              disabled={disabled || noneSelectedInRow}
              labelTextMapper={multiSelectLabelTextMapper}
              onChange={multiSelectOnChange}
              size='sm'
              value={compact([
                ...selectedObjectIds,
                ...(addAllCurrentTemplatesChecked ? [ALL_CURRENT_TEMPLATES] : []),
                ...(addAllCurrentAndFutureTemplatesChecked
                  ? [ALL_CURRENT_AND_FUTURE_TEMPLATES]
                  : []),
              ])}
              selectOptionValues={[
                {
                  label: addAllCurrentAndFutureTemplatesCheckboxLabelTextMapper(),
                  value: ALL_CURRENT_AND_FUTURE_TEMPLATES,
                },
                {
                  label: selectAllLabelTextMapper(),
                  value: ALL_CURRENT_TEMPLATES,
                },
                ...sortBy(
                  (obj) => obj['label'],
                  scopeObjects.map((object) => {
                    return {
                      label: `${object.attributes.name} (${object.id})`,
                      customLabel: objectCustomLabelTextMapper(
                        object,
                        scopeObjects[0].type.startsWith('verification-template/'),
                      ),
                      value: object.id,
                    };
                  }),
                ),
              ]}
            />
            {scopeObjects.some((obj) => obj.type === ApiModel.InquiryTemplate) ? (
              <RelatedVTPermissionsWarning objects={objects} rolePermissions={rolePermissions} />
            ) : null}
          </>
        )}
      </StyledPermissionConfigCell>
    </StyledTableRow>
  );
};

const WarningWrapper = styled.div`
  display: flex;
  margin-top: 6px;
`;

const HelpText = styled(Paragraph).attrs(() => ({ size: 'sm' }))`
  white-space: pre-wrap;
  line-height: ${Spacing[5]};
  max-height: 250px;
  overflow-y: auto;
`;

// show warning unless inquiry template edit perms are not enabled OR
// full verification template perms are already enabled.
const isMissingRelatedPermissions = (rolePermissions: RolePermission[]): boolean => {
  return !(
    !rolePermissions.some((rp) => rp.name === Permission.InquiryTemplateWrite) ||
    rolePermissions.some(
      (rp) => rp.name === Permission.VerificationTemplateWrite && rp['object-id'] == null,
    )
  );
};

interface RelatedVTPermissionsWarningProps {
  objects?: PermissionObjectType[];
  rolePermissions: RolePermission[];
}

export const MISSING_RELATED_PERMISSIONS_LABEL =
  'Further permissions required to fully enable editing.';

/**
 * Component for displaying an inline warning for missing related permissions.
 * Currently only used for missing verification template permissions when enabling
 * inquiry template write permissions.
 */
const RelatedVTPermissionsWarning = ({
  objects,
  rolePermissions,
}: RelatedVTPermissionsWarningProps) => {
  const isMissingVTPermissions = isMissingRelatedPermissions(rolePermissions);
  if (isMissingVTPermissions) {
    // if role permissions contains fully enabled inquiry template write permissions,
    // then we also need fully enabled verification template write permission
    const missingFullVTPermission = rolePermissions.some(
      (rp) => rp.name === Permission.InquiryTemplateWrite && rp['object-id'] == null,
    );
    if (missingFullVTPermission) {
      return (
        <WarningWrapper>
          <Paragraph size='sm'>{MISSING_RELATED_PERMISSIONS_LABEL}</Paragraph>
          <HelpIcon popoverWidth={200}>
            <Paragraph size='sm'>
              For full access, enable editing for all verification templates.
            </Paragraph>
          </HelpIcon>
        </WarningWrapper>
      );
    } else {
      const itmplSinglePermIds = rolePermissions
        .filter((rp) => rp.name === Permission.InquiryTemplateWrite && rp['object-id'] != null)
        .map((rp) => rp['object-id']);
      const vtmplSinglePermIds = rolePermissions
        .filter((rp) => rp.name === Permission.VerificationTemplateWrite && rp['object-id'] != null)
        .map((rp) => rp['object-id'])
        .filter(Boolean);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const itmplSinglePermVtmplIds = objects
        .filter(
          (obj): obj is InquiryTemplate =>
            obj.type === ApiModel.InquiryTemplate && itmplSinglePermIds.includes(obj.id),
        )
        .flatMap((itmpl) => itmpl.attributes['verification-template-ids'])
        .filter(Boolean);
      const missingVtmplIds = difference(itmplSinglePermVtmplIds, vtmplSinglePermIds);
      if (missingVtmplIds.length === 0) {
        return null;
      }
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const missingVtmpls = objects
        .filter((obj) => missingVtmplIds.includes(obj.id))
        .map((vtmpl) => `${vtmpl.attributes.name} (${vtmpl.id})`);
      return (
        <WarningWrapper>
          <Paragraph size='sm'>{MISSING_RELATED_PERMISSIONS_LABEL}</Paragraph>
          <HelpIcon popoverWidth={450}>
            <HelpText>
              For full access, enable editing for these verification templates:
              <ul>
                {missingVtmpls.map((vtmpl) => (
                  <li key={vtmpl}>{`- ${vtmpl}`}</li>
                ))}
              </ul>
            </HelpText>
          </HelpIcon>
        </WarningWrapper>
      );
    }
  } else {
    return <></>;
  }
};

export default PermissionsTable;

const StyledPermissionNameHeaderCell = styled(TableHeaderCell)`
  display: flex;
  min-width: 240px;
`;

const StyledCheckbox = styled(Checkbox)`
  justify-content: center;
`;

const StyledPermissionNameCell = styled(TableCell)`
  display: flex;
  min-width: 240px;
`;

const StyledPermissionConfigCell = styled(TableCell)`
  padding: 0 12px 0 12px;
`;

const StyledMultiSelect = styled(MultiSelect)`
  width: 320px;
`;

const StyledTooltip = styled(Tooltip)`
  padding: 2px 0 0 0;
`;

const StyledAccordionTitle = styled.div`
  display: flex;
`;

const StyledTooltipInner = styled.div`
  max-width: 160px;
`;

const StyledSelect = styled(Select)`
  width: 320px;
`;
